/**
 * Inquiries (Consultas) API service
 * Manages lead capture and inquiry management
 */

import { api } from './api';

// Local type definitions
export const LEAD_INQUIRY_STATUSES = ['new', 'contacted', 'converted', 'dismissed'] as const;
export type LeadInquiryStatus = (typeof LEAD_INQUIRY_STATUSES)[number];

export interface InquiryVehicleInfo {
  make?: string;
  model?: string;
  year?: number;
  registration?: string;
}

export interface Inquiry {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  serviceInterest?: string | null;
  vehicleInfo?: InquiryVehicleInfo | null;
  message: string;
  status: LeadInquiryStatus;
  convertedCustomerId?: string | null;
  convertedBookingId?: string | null;
  assignedTo?: string | null;
  notes?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface CreateInquiryRequest {
  name: string;
  email: string;
  phone?: string;
  serviceInterest?: string;
  vehicleInfo?: InquiryVehicleInfo;
  message: string;
}

export interface UpdateInquiryRequest {
  status?: LeadInquiryStatus;
  assignedTo?: string | null;
  notes?: string;
}

export interface ConvertInquiryRequest {
  customerId?: string;
  vehicleId?: string;
  serviceTypeId?: string;
  preferredDate?: string;
  notes?: string;
}

export interface InquiryWithAssignment extends Inquiry {
  assignedStaffName?: string;
}

export interface InquiryCountSummary {
  new: number;
  contacted: number;
  converted: number;
  dismissed: number;
  total: number;
}

export interface InquiryListResponse {
  inquiries: InquiryWithAssignment[];
  total: number;
  limit: number;
  offset: number;
}

export const inquiriesApi = {
  /**
   * Submit public inquiry form
   */
  submitPublic: (data: CreateInquiryRequest) =>
    api.post<Inquiry>('/api/public/inquiries/public', data),

  /**
   * Get inquiry count summary for dashboard
   */
  getCountSummary: () =>
    api.get<InquiryCountSummary>('/api/inquiries/count'),

  /**
   * List all inquiries with optional status filter
   */
  list: (params?: { status?: LeadInquiryStatus; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return api.get<InquiryListResponse>(
      `/api/inquiries${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get inquiry by ID
   */
  getById: (id: string) =>
    api.get<InquiryWithAssignment>(`/api/inquiries/${id}`),

  /**
   * Update inquiry status/assignment
   */
  update: (id: string, data: UpdateInquiryRequest) =>
    api.patch<Inquiry>(`/api/inquiries/${id}`, data),

  /**
   * Convert inquiry to booking
   */
  convert: (id: string, data: ConvertInquiryRequest) =>
    api.post<Inquiry>(`/api/inquiries/${id}/convert`, data),

  /**
   * Delete inquiry
   */
  delete: (id: string) =>
    api.delete<void>(`/api/inquiries/${id}`),
};
